/* eslint-disable react/react-in-jsx-scope */
export const getDotClass = (matchLevel) => {
  switch (matchLevel) {
    case "full":
      return "dot red";
    case "partial":
      return "dot yellow";
    default:
      return null;
  }
};

export const getFieldMatchLevel = (primaryValue, rowValue) => {
  if (!primaryValue || primaryValue === "-" || !rowValue || rowValue === "-") return null;
  const p = String(primaryValue).toLowerCase().trim();
  const r = String(rowValue).toLowerCase().trim();
  if (p === r) return "full";
  if (p.includes(r) || r.includes(p)) return "partial";
  return null;
};

export const safe = (value) => (value !== null && value !== undefined && value !== ""
  ? value
  : "-");

/* ================= DOCUMENT ROWS ================= */
export const mapDocumentRows = (documents = []) => documents.map((doc) => {
  const status = doc.status || "-";
  const score = doc.qualityScore !== undefined && doc.qualityScore !== null
    ? `Score: ${doc.qualityScore}`
    : null;
  return {
    documentId: doc.documentId || doc.caseId || doc.case_id || "-",
    documentType: doc.documentType || "-",
    submitDate: doc.submitDate || "-",
    fileSize: doc.fileSize || "-",
    documentName: doc.documentName || doc.filename || "-",
    description: doc.description || "-",
    notes: score ? `${status} (${score})` : status,
    fileUrl: doc.fileUrl || "-",
    caseId: doc.caseId || doc.case_id || "-",
  };
});

export const extractPrimaryComparableFields = (primary) => {
  if (!primary?.details) return {};
  const findSection = (title) => primary.details.find((d) => d.title === title)?.fields || {};
  const contact = findSection("Contact Details");
  const mailing = findSection("Mailing Address");
  const property = findSection("Property Address");
  return {
    // CONTACT
    primaryPhone: contact.primaryPhone,
    secondaryPhone: contact.secondaryPhone,
    email: contact.email,
    // MAILING
    mailStreet: mailing.street,
    mailCity: mailing.city,
    mailState: mailing.state,
    mailZip: mailing.zip,
    // PROPERTY
    propStreet: property.street,
    propCity: property.city,
    propState: property.state,
    propZip: property.zip,
  };
};

export const extractDuplicateComparableFields = (duplicates = []) => ({
  primaryPhone: duplicates.map((d) => d.primaryPhone),
  secondaryPhone: duplicates.map((d) => d.secondaryPhone),
  email: duplicates.map((d) => d.email),
  mailStreet: duplicates.map((d) => d.mailStreet),
  mailCity: duplicates.map((d) => d.mailCity),
  mailState: duplicates.map((d) => d.mailState),
  mailZip: duplicates.map((d) => d.mailZip),
  propStreet: duplicates.map((d) => d.propStreet),
  propCity: duplicates.map((d) => d.propCity),
  propState: duplicates.map((d) => d.propState),
  propZip: duplicates.map((d) => d.propZip),
});

export const parseApplicantName = (rawName = "") => {
  if (!rawName || !rawName.includes(",")) {
    return {
      firstName: rawName || "-",
      lastName: "-",
    };
  }

  const [lastName, firstName] = rawName.split(",").map((v) => v.trim());

  return {
    firstName: firstName || "-",
    lastName: lastName || "-",
  };
};

export const mapApiDuplicatesToTableRows = (apiMatches = []) => apiMatches

  .filter((item) => !item.is_self_match)
  .map((item) => {
    const record = item?.raw
      || item?.record?.full_candidate_record
      || item?.match_record || {};
    const rawName = record?.ApplicantFirstName
      || record?.ApplicantName
      || "";
    const { firstName, lastName } = parseApplicantName(rawName);
    const similarity = item?.final_similarity
      ?? item?.matches?.final_similarity
      ?? item?.similarity?.final_similarity;
    return {
      caseId: record.CaseID || item.case_id || "-",
      submitDate: record.CaseSubmitDate || "-",
      origin: record.IntakeOrigin || "-",
      firstName: firstName || record.ApplicantFirstName || "-",
      lastName: record.ApplicantLastName || "-",
      caseManager: record.CaseManager || "-",
      primaryPhone: record.ApplicantPrimaryPhone || "-",
      secondaryPhone: record.ApplicantSecondaryPhone || "-",
      email: record.ApplicantEmailAddress || "-",
      mailStreet: record.ApplicantStreetAddress || "-",
      mailCity: record.ApplicantCity || "-",
      mailState: record.ApplicantState || "-",
      mailCounty: record.ApplicantCounty || "-",
      mailZip: record.ApplicantZip || "-",
      propStreet: record.PropertyStreetAddress || "-",
      propCity: record.PropertyCity || "-",
      propState: record.PropertyState || "-",
      propCounty: record.PropertyCounty || "-",
      propZip: record.PropertyZip || "-",
      status: item.status || "-",
      dataSource:
        item?.data_source
        || record?.Program
        || record?.DataSource
        || record?.IntakeOrigin
        || "BENEFITS",
      notes:
        similarity !== undefined
          ? `${similarity}% Match`
          : "-",
      // status:
      //   item.is_exact_duplicate
      //     ? "exact"
      //     : item.is_likely_duplicate
      //       ? "partial"
      //       : "",
    };
  });

export const mapInputRecordToPrimaryRow = (inputRecord = {}, duplicateComparable = {}) => ({
  caseId: safe(inputRecord.CaseID),
  submitDate: safe(inputRecord.CaseSubmitDate),
  origin: safe(inputRecord.IntakeOrigin),
  firstName: safe(inputRecord.ApplicantFirstName),
  lastName: safe(inputRecord.ApplicantLastName),
  caseManager: safe(inputRecord.CaseManager),
  caseManagerUpdated: {
    date: safe(inputRecord.CaseSubmitDate),
    time: "-",
  },
  notes: inputRecord.final_similarity !== undefined
    ? `${inputRecord.final_similarity}% Match`
    : "Primary case identified by match",
  details: [
    /* ================= STATUSES ================= */
    {
      title: "Statuses",
      fields: {
        intakeStatus: (
          <span className="pill blue">
            {safe(inputRecord.IntakeOrigin) !== "-"
              ? "Intake Completed"
              : "-"}
          </span>
        ),
        intakeStatusUpdated: safe(inputRecord.CaseSubmitDate),
        activityStatus: (
          <span className="pill green">
            {safe(inputRecord.CaseID) !== "-" ? "In Progress" : "-"}
          </span>
        ),
        activityStatusUpdated: safe(inputRecord.CaseSubmitDate),
        activityStatusDetails: safe(inputRecord.CaseID),
        workflowStatus: (
          <span className="pill green">
            {safe(inputRecord.CaseManager) !== "-"
              ? "Active"
              : "-"}
          </span>
        ),
        workflowStatusUpdated: safe(inputRecord.CaseSubmitDate),
      },
    },
    /* ================= CONTACT ================= */
    {
      title: "Contact Details",
      fields: {
        primaryPhone: safe(inputRecord.ApplicantPrimaryPhone) !== "-" ? (
          <span className="value-with-dot">
            <span
              className={getDotClass(
                getFieldMatchLevel(
                  inputRecord.ApplicantPrimaryPhone,
                  duplicateComparable.primaryPhone,
                ),
              )}
            />
            {inputRecord.ApplicantPrimaryPhone}
          </span>
        ) : "-",
        secondaryPhone: safe(inputRecord.ApplicantSecondaryPhone) !== "-" ? (
          <span className="value-with-dot">
            <span
              className={getDotClass(
                getFieldMatchLevel(
                  inputRecord.ApplicantSecondaryPhone,
                  duplicateComparable.secondaryPhone,
                ),
              )}
            />
            {inputRecord.ApplicantSecondaryPhone}
          </span>
        ) : "-",
        email: safe(inputRecord.ApplicantEmailAddress) !== "-" ? (
          <span className="value-with-dot">
            <span
              className={getDotClass(
                getFieldMatchLevel(
                  inputRecord.ApplicantEmailAddress,
                  duplicateComparable.email,
                ),
              )}
            />
            {inputRecord.ApplicantEmailAddress}
          </span>
        ) : "-",
      },
    },
    /* ================= PERSONAL ================= */
    {
      title: "Personal Details",
      fields: {
        dateOfBirth: safe(inputRecord.ApplicantDateOfBirth),
        gender: "-",
        race: "-",
        ethnicity: "-",
      },
    },
    /* ================= MAILING ================= */
    {
      title: "Mailing Address",
      fields: {
        street: safe(inputRecord.ApplicantStreetAddress) !== "-" ? (
          <span className="value-with-dot">
            <span
              className={getDotClass(
                getFieldMatchLevel(
                  inputRecord.ApplicantStreetAddress,
                  duplicateComparable.mailStreet,
                ),
              )}
            />
            {inputRecord.ApplicantStreetAddress}
          </span>
        ) : "-",
        city: safe(inputRecord.ApplicantCity),
        state: safe(inputRecord.ApplicantState),
        zip: safe(inputRecord.ApplicantZip),
        county: safe(inputRecord.ApplicantCounty),
      },
    },
    /* ================= PROPERTY ================= */
    {
      title: "Property Address",
      fields: {
        street: safe(inputRecord.PropertyStreetAddress) !== "-" ? (
          <span className="value-with-dot">
            <span
              className={getDotClass(
                getFieldMatchLevel(
                  inputRecord.PropertyStreetAddress,
                  duplicateComparable.propStreet,
                ),
              )}
            />
            {inputRecord.PropertyStreetAddress}
          </span>
        ) : "-",
        city: safe(inputRecord.PropertyCity),
        state: safe(inputRecord.PropertyState),
        zip: safe(inputRecord.PropertyZip),
        county: safe(inputRecord.PropertyCounty),
      },
    },
  ],
});

export const getFirstNonEmpty = (sources) => sources.find((arr) => arr.length > 0) || [];
export const getReportTitle = ({ reportType, reportData }) => {
  const caseId = reportData?.metadata?.case_id
    ?? reportData?.input_record?.CaseID
    ?? 'N/A';

  switch (reportType) {
    case 'APPLICATION_STATUS':
      return `Application Status Report: ${caseId}`;
    case 'BENEFITS':
      return `Benefits Report: ${caseId}`;
    case 'ELIGIBILITY':
      return `Eligibility Report: Case ${caseId}`;
    default:
      return `Duplicate Report: Application ${caseId}`;
  }
};

export const sortWeekDays = (data) => {
  const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return data.sort(
    (a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day),
  );
};